<script setup lang="ts">
import { cva, type VariantProps } from 'class-variance-authority'
import { type IconName } from '@/components/icons'
import type { ButtonHTMLAttributes } from 'vue'
import BaseIcon from '../BaseIcon/BaseIcon.vue'
import type { RouterLinkProps } from 'vue-router'

const button = cva(
  'button overflow-hidden rounded font-semibold flex items-center transition-colors',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-white hover:bg-primary-hover active:bg-primary-active hover:text-white',
        error: 'bg-error text-white hover:bg-error/95 active:bg-error/90',
        secondary: 'bg-secondary text-white hover:bg-secondary-hover active:bg-secondary-active',
        gray: 'bg-gray-2 text-secondary hover:bg-gray-button-hover active:bg-gray-button-active hover:text-secondary',
        'gray-red':
          'bg-gray-2 text-red-5 hover:bg-gray-button-hover active:bg-gray-button-active hover:text-red-6',
        orange: 'bg-orange text-white hover:bg-orange-6 active:bg-orange-7',
        outline:
          'bg-black/0 text-secondary border border-gray-line hover:bg-black/5 active:bg-black/10',
      },
      size: {
        small: 'px-[15px] pt-[7px] pb-[8px] h-[40px]',
        medium: 'px-[20px] pt-[12px] pb-[13px] h-[50px]',
      },
      disabled: {
        true: '!bg-gray-button !text-gray-line',
      },
    },
    // compoundVariants: [{ intent: 'primary', size: 'medium', class: 'primaryMedium' }]
  },
)

export type ButtonProps = VariantProps<typeof button>
type Props = {
  /** Тип кнопки визуальный */
  variant?: ButtonProps['variant']

  /** Размер кнопки */
  size?: ButtonProps['size']

  /** Текст кнопки */
  text?: string

  /** Название иконки */
  icon?: IconName

  /** Тип кнопки в HTML */
  type?: ButtonHTMLAttributes['type']

  /** Определяет, выключена ли кнопка */
  disabled?: boolean

  /** Определяет, выключена ли кнопка только визуально. Она все еще кликабельна */
  disabledVisually?: boolean

  /** Ссылка, куда ведет кнопка. При использовании этого пропа кнопка становится ссылкой */
  to?: RouterLinkProps['to']
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'medium',
  type: 'button',
})
</script>

<template>
  <RouterLink
    v-if="props.to"
    :to="props.to"
    :class="button({ variant, size, disabled: props.disabled || props.disabledVisually })"
    class="flex gap-2.5 disabled:bg-gray-button disabled:text-gray-line"
    :disabled="props.disabled"
  >
    <BaseIcon v-if="props.icon" :icon="props.icon" />
    <span
      v-if="text"
      class="text-base"
      :class="{
        'mr-2.5': size === 'medium',
        'mr-[5px]': size === 'small',
        'ml-2.5': size === 'medium' && !props.icon,
        'ml-[5px]': size === 'small' && !props.icon,
      }"
    >
      {{ text }}
    </span>
  </RouterLink>
  <button
    v-else
    :class="button({ variant, size, disabled: props.disabled || props.disabledVisually })"
    class="flex gap-2.5 disabled:bg-gray-button disabled:text-gray-line"
    :type="props.type"
    :disabled="props.disabled"
  >
    <BaseIcon v-if="props.icon" :icon="props.icon" />
    <span
      v-if="text"
      class="text-base"
      :class="{
        'mr-2.5': size === 'medium',
        'mr-[5px]': size === 'small',
        'ml-2.5': size === 'medium' && !props.icon,
        'ml-[5px]': size === 'small' && !props.icon,
      }"
    >
      {{ text }}
    </span>
  </button>
</template>

<style scoped></style>
